import { Injectable, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { firstValueFrom } from 'rxjs';
import { ConfirmationDialogComponent } from '../components/confirmation-dialog/confirmation-dialog.component';
import { AuthService } from '../services/auth.service';
import { EventBusService } from '../services/eventBus.service';
import { PaymentService } from '../services/payment.service';
import { StorageEventsService } from '../services/storageEvents.service';
import { FeatureFlagsService } from '../services/feature-flags.service';
import { SessionRecordingService } from '../services/session-recording.service';
import { ZendeskService } from '../services/zendesk.service';
import { StorageService } from '../shared/storage.service';
import { EVENTS, STORAGE_KEYS } from '../utils/constants';
import { getInitials } from '../utils/utils';
import { environment } from 'src/environments/environment';
import { BankAccountsStore } from './bank-accounts.store';
import { ServiceAccessStore } from './service-access.store';
import { DebtsStore } from './debts.store';
import { PaymentLinkStore } from './payment-link.store';
import { AppConfig, BankAccount, Customer, CustomerType, KycStatus, User } from './types';
import { MovementsStore } from './movements.store';
import { ExtractionRequestsStore } from './extraction-requests.store';
import { BeneficiariesStore } from './beneficiaries.store';
import { BankBeneficiariesStore } from './bank-beneficiaries.store';
import { BankExtractionRequestsStore } from './bank-extraction-requests.store';

// Constants for tab synchronization events
const LOGOUT_EVENT_KEY = 'superpay_logout_event';
const LOGIN_EVENT_KEY = 'superpay_login_event';
const FORGOT_PASSWORD_KEY = 'forgotPasswordSuccess';
// Event for notifying NewPaymentStore to clear data
const CLEAR_NEW_PAYMENT_STORE = 'clear_new_payment_store';

@Injectable({
  providedIn: 'root'
})
export class AuthStore {
  private encryptedPass = signal<string | undefined>(undefined);
  public closingSession = signal(false);
  public user = signal<User | undefined>(undefined);
  public token = signal<string | undefined>(undefined);
  public customer = signal<Customer | undefined>(undefined);
  appConfig = signal<AppConfig | undefined>(null);

  public isPersonal = computed(() => this.customer()?.customerType === CustomerType.PERSONAL);
  public isBusiness = computed(() => this.customer()?.customerType === CustomerType.BUSINESS);

  public isKycApproved = computed(() => this.customer()?.kycStatus === KycStatus.APPROVED);

  /**
   * Estado KYC efectivo: prioriza el registro de Identity
   * (customer.metadata.identityVerification) sobre el campo persistido
   * customer.kycStatus, salvo cuando el campo está en PENDING. Es la misma
   * lógica que usa la pantalla de ajustes KYC, centralizada aquí para que la
   * pantalla y los flujos de extracción compartan una única fuente de verdad.
   */
  public kycStatusEffective = computed<KycStatus | undefined>(() => {
    const customer = this.customer() as any;
    // El campo `kycStatus` en PENDING manda: se ignora cualquier registro de
    // Identity (que podría no haberse propagado) y se trata como "No iniciado".
    if (customer?.kycStatus === KycStatus.PENDING) return KycStatus.PENDING;
    const iv = customer?.metadata?.identityVerification;
    if (iv) {
      // Sesión cancelada — p. ej. reemplazada (superseded) por una nueva al
      // reintentar el KYC. Identity la marca con finalStatus 'rejected', pero
      // no es un rechazo real ni consume reintentos: equivale a "No iniciado".
      // Por eso se evalúa ANTES que finalStatus.
      if (iv.status === 'cancelled') return KycStatus.PENDING;
      if (iv.finalStatus === 'verified') return KycStatus.APPROVED;
      if (iv.finalStatus === 'rejected') return KycStatus.REJECTED;
      // Revisión manual por un operador de Identity: el resultado final llega
      // después por webhook.
      if (iv.finalStatus === 'needs_review') return KycStatus.IN_REVIEW;
      // Aprobación revocada por administración: el cliente puede volver a
      // verificarse, equivale a "No iniciado".
      if (iv.finalStatus === 'revoked') return KycStatus.PENDING;
      if (iv.status === 'expired') return KycStatus.EXPIRED;
      // `failed` consume reintento (equivale a rechazo).
      if (iv.status === 'failed') return KycStatus.REJECTED;
      // El usuario terminó el formulario y falta el veredicto del operador.
      if (iv.status === 'completed') return KycStatus.IN_REVIEW;
      // Cualquier otro estado en curso (pending, docs_*, liveness_*, matching)
      // es una verificación que el usuario NO terminó (p. ej. cerró el
      // formulario a mitad): equivale a "No iniciado" para que pueda
      // iniciarla de nuevo, en vez de quedar atascado en "En revisión".
      return KycStatus.PENDING;
    }
    return customer?.kycStatus;
  });

  /** KYC verificado considerando el registro de Identity (no solo el campo persistido). */
  public isKycVerified = computed(() => this.kycStatusEffective() === KycStatus.APPROVED);

  /**
   * Resultado de `GET /auth/feature-flags` para el usuario en sesión: si el KYC
   * está habilitado para él cuando el modo es 'selected'. La lista de correos
   * habilitados se quedó en el backend, así que esto no se puede decidir aquí.
   */
  private kycEnabledForUser = signal(false);

  /**
   * Habilitación de KYC por configuración: `enable_kyc_verification` activo y
   * `kyc_verification_enabled_for` igual a 'all', o bien el backend confirmando
   * que este usuario está en la lista habilitada.
   *
   * El interruptor se parsea explícitamente (boolean o el string "true", como el
   * resto de flags de config): un truthy check dejaría el string 'false' como
   * habilitado. El modo es fail-closed: cualquier valor que no sea 'all' ni
   * 'selected' (ausente, typo, valor nuevo) deja el KYC deshabilitado.
   */
  public isKycFeatureEnabled = computed(() => {
    const cfg = this.appConfig() as any;
    const enabled = cfg?.enable_kyc_verification === true || cfg?.enable_kyc_verification === 'true';
    if (!enabled) return false;

    const mode = String(cfg?.kyc_verification_enabled_for ?? '').trim().toLowerCase();
    if (mode === 'all') return true;
    if (mode === 'selected') return this.kycEnabledForUser();
    return false;
  });

  /**
   * Habilitación del KYC de empresa por configuración (`enabled_business_kyc`).
   * Controla la visibilidad de la sección "KYC Empresa" en Ajustes. Acepta tanto
   * boolean como el string "true".
   */
  public isBusinessKycEnabled = computed(() => {
    const raw = (this.appConfig() as any)?.enabled_business_kyc;
    return raw === true || raw === 'true';
  });

  /**
   * Lee la config `kyc_required_for_extractions`. Cuando está activa, todas las
   * operaciones de extracción (transferencia interna, pagos a tarjetas/billetera,
   * pagos bancarios, pago de servicios y pago por merchant) exigen KYC aprobado.
   * Acepta tanto boolean como el string "true".
   */
  public isKycRequiredForExtractionsConfig = computed(() => {
    const raw = this.appConfig()?.kyc_required_for_extractions as unknown;
    return raw === true || raw === 'true';
  });

  /**
   * Lee la config `kyc_required_after_first_extraction`. Cuando está activa, el
   * KYC se exige a partir de la segunda operación de extracción (la primera
   * exitosa marca `customer.metadata.firstExtractionAt`). Acepta tanto boolean
   * como el string "true".
   */
  public isKycRequiredAfterFirstExtractionConfig = computed(() => {
    const raw = this.appConfig()?.kyc_required_after_first_extraction as unknown;
    return raw === true || raw === 'true';
  });

  /** El customer ya consumió su operación de extracción libre. */
  public hasCompletedFirstExtraction = computed(
    () => !!(this.customer() as any)?.metadata?.firstExtractionAt
  );

  /**
   * Para operaciones de extracción: el KYC es obligatorio — por la política
   * total (`kyc_required_for_extractions`) o por la política "después de la
   * primera" (`kyc_required_after_first_extraction` + flag ya marcado) — pero el
   * customer aún no está verificado. Si es true, la operación debe redirigir a
   * la pantalla de ajustes KYC en lugar de continuar.
   */
  public isKycRequiredForExtraction = computed(
    () =>
      (this.isKycRequiredForExtractionsConfig() ||
        (this.isKycRequiredAfterFirstExtractionConfig() && this.hasCompletedFirstExtraction())) &&
      !this.isKycVerified()
  );

  /**
   * KYC de empresa aprobado. El estado representativo lo mantiene el Core en
   * `business.businessKycStatus` y viaja con el customer (populate), así que es
   * reactivo sin necesidad de un fetch aparte.
   */
  public isBusinessKycVerified = computed(
    () => (this.customer()?.business as any)?.businessKycStatus === KycStatus.APPROVED
  );

  public appMetadata = computed(
    () => this.user()?.metadata?.applicationsMetadata?.superpay23 || {}
  );
  public typeTwoFA = computed(() => this.user()?.metadata?.twoFactorType);

  public isEmailTwoFa = computed(() => this.typeTwoFA() === 'email');
  public isAppTwoFa = computed(() => this.typeTwoFA() === 'appAuth');
  public hasTwoFa = computed(() => this.isEmailTwoFa() || this.isAppTwoFa());

  public metadata = computed(() => this.user()?.metadata || {});

  /**
   * Ya se le mostró en esta sesión el diálogo opcional de KYC de la primera
   * operación de extracción ("Realizar KYC" / "Más tarde"). Evita repetirlo en
   * cada gate (route guard + acción) dentro de la misma sesión.
   */
  private firstExtractionKycPrompted = false;

  /** Ya se mostró en esta sesión el aviso (no bloqueante) de KYC de empresa. */
  private businessKycPromptShown = false;

  /**
   * Gate compartido para operaciones de extracción. Si el KYC es obligatorio
   * (política total o "después de la primera" con el flag ya marcado) y el
   * customer aún no está verificado, informa, redirige a la pantalla de KYC y
   * devuelve false para abortar la operación.
   *
   * En la PRIMERA operación con la política "después de la primera" activa el
   * KYC es opcional: se muestra (una vez por sesión) un diálogo invitando a
   * verificarse — "Realizar KYC" redirige a /settings/kyc y aborta; "Más tarde"
   * (o cerrar el diálogo) deja continuar la operación.
   *
   * Devuelve true cuando la operación puede continuar. Lo usan tanto el route
   * guard (páginas) como las acciones/diálogos de extracción.
   */
  public async ensureKycApprovedForExtraction(
    message = 'Debes completar la verificación de identidad (KYC) para realizar esta operación.'
  ): Promise<boolean> {
    // 1) KYC de PERSONA (verificación personal del titular): aplica a TODAS las
    //    cuentas, sean personales o de empresa. Se basa en customer.kycStatus.
    if (this.isKycRequiredForExtraction()) {
      this.matSnackBar.open(message, 'Entendido', {
        duration: 6000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
      this.router.navigate(['/settings/kyc']);
      return false;
    }

    // 2) KYC de EMPRESA (documentos de la empresa): solo para cuentas de empresa
    //    y NUNCA bloqueante — se limita a invitar a completarlo. Si el usuario
    //    acepta ir a la pantalla, se aborta esta operación (se le lleva allí);
    //    si dice "Más tarde", se continúa.
    if (this.isBusiness()) {
      const ok = await this.promptBusinessKycForExtraction();
      if (!ok) return false;
    }

    if (
      this.isKycRequiredAfterFirstExtractionConfig() &&
      !this.isKycVerified() &&
      !this.hasCompletedFirstExtraction() &&
      !this.firstExtractionKycPrompted
    ) {
      this.firstExtractionKycPrompted = true;
      const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
        width: '420px',
        data: {
          title: 'Verificación de identidad (KYC)',
          description:
            'Aún no has completado la verificación de identidad (KYC). ' +
            '¿Deseas realizar la verificación ahora?',
          confirmText: 'Realizar KYC',
          cancelText: 'Más tarde',
          showInfoIcon: true
        }
      });
      const doKycNow = await firstValueFrom(dialogRef.afterClosed());
      if (doKycNow) {
        this.router.navigate(['/settings/kyc']);
        return false;
      }
    }

    return true;
  }

  /**
   * Invitación (NO bloqueante) a completar el KYC de empresa, para cuentas de
   * empresa que aún no lo han iniciado (o cuyo KYC quedó rechazado / caducado /
   * pendiente de más información). El KYC de empresa no es obligatorio:
   * el usuario puede responder "Más tarde" y seguir con la operación. Solo se
   * aborta cuando él mismo elige ir a completarlo, porque entonces se le lleva a
   * /settings/company-kyc.
   *
   * Se muestra una vez por sesión.
   */
  private async promptBusinessKycForExtraction(): Promise<boolean> {
    // No aplica: el KYC de empresa no está habilitado o ya está aprobado. No se
    // muestra ninguna alerta ni se interrumpe la operación.
    if (!this.isBusinessKycEnabled() || this.isBusinessKycVerified()) {
      return true;
    }

    // Tampoco aplica cuando el KYC de empresa ya fue enviado y está en curso
    // (PENDING / IN_REVIEW): invitar a "Completar KYC" sería engañoso y
    // repetitivo. Solo se invita cuando aún no se ha iniciado o quedó en un
    // estado que exige acción del usuario (REJECTED, ADDITIONAL_INFO_REQUIRED,
    // EXPIRED).
    const businessKycStatus = (this.customer()?.business as any)?.businessKycStatus;
    if (businessKycStatus === KycStatus.PENDING || businessKycStatus === KycStatus.IN_REVIEW) {
      return true;
    }

    // Si el aviso de KYC PERSONAL está pendiente en esta misma ejecución (el
    // bloque de "primera extracción" de ensureKycApprovedForExtraction), no se
    // muestra también el de empresa: se evita encadenar dos diálogos seguidos.
    // El de empresa se mostrará en una operación posterior.
    const personalPromptPending =
      this.isKycRequiredAfterFirstExtractionConfig() &&
      !this.isKycVerified() &&
      !this.hasCompletedFirstExtraction() &&
      !this.firstExtractionKycPrompted;

    if (!personalPromptPending && !this.businessKycPromptShown) {
      this.businessKycPromptShown = true;
      const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
        width: '420px',
        data: {
          title: 'KYC de empresa',
          description:
            'Te recomendamos completar el KYC de tu empresa. Puedes seguir operando ' +
            'por ahora. ¿Deseas completarlo ahora?',
          confirmText: 'Completar KYC',
          cancelText: 'Más tarde',
          showInfoIcon: true
        }
      });
      const doKycNow = await firstValueFrom(dialogRef.afterClosed());
      if (doKycNow) {
        this.router.navigate(['/settings/company-kyc']);
        return false;
      }
    }

    return true;
  }

  /**
   * Gate para acceder al detalle de una subasta / pujar: el KYC es OBLIGATORIO y
   * estricto (no hay "primera operación opcional" como en extracciones). Si la
   * verificación KYC está habilitada y el customer no está aprobado, informa,
   * redirige a /settings/kyc y bloquea el acceso. Es la misma exigencia que
   * aplica el backend en `placeBid`.
   */
  public ensureKycApprovedForAuction(
    message = 'Debes tener el KYC aprobado para acceder a las subastas.'
  ): boolean {
    // Bloqueo ABSOLUTO: el KYC aprobado es obligatorio para entrar/ver una subasta,
    // independientemente del feature-flag `enable_kyc_verification` (la app de
    // subastas exige KYC siempre, igual que el backend en `get`/`placeBid`). Es
    // fail-closed: si el customer aún no cargó, `isKycVerified()` es false y se
    // bloquea en vez de dejar pasar.
    if (!this.isKycVerified()) {
      this.matSnackBar.open(message, 'Entendido', {
        duration: 6000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
      this.router.navigate(['/settings/kyc']);
      return false;
    }
    return true;
  }

  /**
   * Update optimista del flag de primera extracción en el signal local tras una
   * operación de salida exitosa, para que el gate funcione en la misma sesión
   * sin recargar. El valor real lo persiste el backend (write-once); en el
   * próximo refresh del customer llega el timestamp definitivo.
   */
  public markFirstExtractionLocally(): void {
    this.customer.update((customer: any) => {
      if (!customer) return customer;
      if (customer.metadata?.firstExtractionAt) return customer;
      return {
        ...customer,
        metadata: { ...customer.metadata, firstExtractionAt: new Date().toISOString() }
      };
    });
  }

  setMetadata(meta: any) {
    this.user.update((user) => {
      return user ? { ...user, metadata: { ...user.metadata, ...meta } } : undefined;
    });
  }

  public personFullName = computed(() => {
    const person = this.customer()?.person;
    return this.isPersonal() ? `${person.firstName} ${person.lastName}` : null;
  });
  public businessFullName = computed(() => {
    const business = this.customer()?.business;
    return this.isBusiness() ? `${business.businessName}` : null;
  });

  userFullName = computed(() => {
    return this.isPersonal() ? this.personFullName() : this.businessFullName();
  });

  public userInitials = computed(() =>
    this.isPersonal()
      ? getInitials(this.personFullName())
      : getInitials(this.customer()?.business.businessName)
  );

  preventReaload = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private paymentService: PaymentService,
    private bankAccountsStore: BankAccountsStore,
    private serviceAccessStore: ServiceAccessStore,
    private eventBusService: EventBusService,
    private storageEventsService: StorageEventsService,
    private storageService: StorageService,
    private matSnackBar: MatSnackBar,
    private matDialog: MatDialog,
    private zendeskService: ZendeskService,
    private paymentLinkStore: PaymentLinkStore,
    private debtsStore: DebtsStore,
    private movementStore: MovementsStore,
    private extractionRequestsStore: ExtractionRequestsStore,
    private beneficiariesStore: BeneficiariesStore,
    private bankBeneficiariesStore: BankBeneficiariesStore,
    private bankExtractionRequestsStore: BankExtractionRequestsStore,
    private sessionRecording: SessionRecordingService,
    private featureFlags: FeatureFlagsService
  ) {
		// El appConfig se carga en APP_INITIALIZER antes de que la app arranque
		// Aquí solo configuramos el intervalo para refrescar cada 2 minutos
		setInterval(() => {
			this.loadAppConfig()
		}, 1000 * 60 * 2);

    this.eventBusService.on(EVENTS.logout_user).subscribe((data) => {
      this.logout();
    });
    // En modo cookie el JWT vive en la cookie httpOnly, no en localStorage. NO borramos aún
    // el token legacy: getUserFromToken() lo usa una vez para el canje transparente a cookie
    // (migración de deploy) y luego lo elimina. En modo cookie el signal no se envía como
    // header. En modo header sí se carga desde storage.
    if (environment.useCookieAuth) {
      this.token.set(undefined);
    } else {
      this.token.set(this.storageService.get(STORAGE_KEYS.AUTH_TOKEN));
    }

    // Subscribe to logout events from other tabs
    this.storageEventsService.on(LOGOUT_EVENT_KEY).subscribe((data) => {
      if (data?.data?.reason === 'inactivity') {
        this.matSnackBar.open('Se cerró la sesión por inactividad', 'Ok', {
          duration: 99999
        });
      }
      this.performLocalLogout();
    });

    // Subscribe to login events from other tabs
    this.storageEventsService.on(LOGIN_EVENT_KEY).subscribe((data) => {
      if (!this.preventReaload) {
        window.location.reload();
      }
      this.preventReaload = false;
    });

    // Subscribe to forgot password events
    this.storageEventsService.on(FORGOT_PASSWORD_KEY).subscribe((data) => {
      if (data && data?.success) {
        // Close this window if it's not the main window
        window.close();
      }
    });

    // Inicializar Zendesk al cargar el servicio
    this.initZendesk();
  }

  // Inicializa el servicio de Zendesk
  private initZendesk(): void {
    this.zendeskService.initialize();
  }

  // La contraseña del cambio forzado (shouldChangePassword) se mantiene SOLO en
  // memoria durante el flujo. Antes se persistía en sessionStorage con un base64
  // reversible (recuperable ante cualquier XSS); eso se eliminó. Si un reload la
  // pierde, el AuthGuard reenvía a /login?redirect=/change-password y se repuebla.
  setEncryptedPass(password) {
    this.encryptedPass.set(password);
  }

  removeEncryptedPass() {
    this.encryptedPass.set(undefined);
  }

  getEncryptedPass() {
    return this.encryptedPass();
  }

  // For other components to use when password reset is successful
  public notifyPasswordResetSuccess(): void {
    this.storageEventsService.emit(FORGOT_PASSWORD_KEY, { success: true });
  }

  isAuthenticated() {
    // Modo cookie (Fase 2): el JWT es httpOnly (no legible por JS). El estado de sesión
    // se deriva de si hay usuario cargado; el guard lo puebla vía getUserFromToken()
    // (que consulta /auth/me con la cookie). Ver auth.guard del front.
    if (environment.useCookieAuth) return !!this.user();
    const token = this.storageService.get(STORAGE_KEYS.AUTH_TOKEN);
    if (!token) return false;
    const { isExpired } = this.isExpiredToken(token);
    return !isExpired;
  }

  saveUserData(accessToken, user, bankAccounts?: BankAccount[], customer?: Customer) {
    try {
      // Modo cookie: el backend ya dejó el JWT en la cookie httpOnly; NO lo persistimos en
      // localStorage ni en el signal (así getClientFromToken usa la cookie, no un header).
      if (!environment.useCookieAuth) {
        this.token.set(accessToken);
        this.storageService.save(STORAGE_KEYS.AUTH_TOKEN, accessToken);
      }

      this.setUserAndCustomer(user, customer, bankAccounts);

      // Solo graba si la configuración lo permite para este usuario (ver
      // SessionRecordingService). No se espera: no debe retrasar el login.
      this.sessionRecording.identifyUser(user, this.userFullName());
      this.loadUserFeatureFlags(user);

      // Autenticar al usuario en Zendesk después de iniciar sesión
      // this.zendeskService.authenticateUser();
      this.zendeskService.initialize({
        email: user.email
      });

      // Marcar que es un login reciente para abrir el diálogo de selección de cuenta
      sessionStorage.setItem('just_logged_in', 'true');

      // Notify other tabs about the login using the StorageEventsService
      this.preventReaload = true;
      this.storageEventsService.emit(LOGIN_EVENT_KEY);
    } catch (err) {}
  }

  // sp_session es httpOnly (no legible por JS); sp_csrf sí es legible y se setea junto a la
  // sesión → lo usamos como proxy de "ya hay sesión por cookie".
  private hasCookieSession(): boolean {
    if (typeof document === 'undefined' || !document.cookie) return false;
    return /(?:^|;\s*)sp_csrf=/.test(document.cookie);
  }

  async getUserFromToken() {
    // Canje transparente (migración de deploy a cookie-mode): si el cliente venía con el
    // token en localStorage y aún no tiene cookie de sesión, lo canjea UNA vez por la cookie
    // httpOnly (no se desloguea). Luego descarta el token legacy.
    if (environment.useCookieAuth) {
      const legacy = this.storageService.get<string>(STORAGE_KEYS.AUTH_TOKEN);
      if (legacy && !this.hasCookieSession()) {
        try {
          await this.authService.migrateSession(legacy);
        } catch {
          /* token legacy inválido/expirado → seguirá el flujo normal (sin sesión) */
        }
      }
      if (legacy) {
        this.storageService.remove(STORAGE_KEYS.AUTH_TOKEN);
        this.token.set(undefined);
      }
    }
    try {
      const result = await this.authService.getClientFromToken(this.token());

      if (result.data?.user?.email) {
        const { user, customer, bankAccounts } = result.data;
        this.setUserAndCustomer(user, customer, bankAccounts);

        // Identificar al usuario en LogRocket al restaurar sesión (si le toca)
        this.sessionRecording.identifyUser(user, this.userFullName());
        this.loadUserFeatureFlags(user);

        // Si el usuario ya tiene una sesión activa, autenticar con Zendesk
        if (this.isAuthenticated()) {
          // this.zendeskService.authenticateUser();
          this.zendeskService.initialize({
            username: result.data.username,
            email: result.data.email
          });
        }
      } else if (result.error) {
        this.logout();
      }
    } catch (err) {
      this.logout();
    }
  }

  setUserAndCustomer(user, customer, bankAccounts) {
    this.user.set(user);

    const defaultBankAccount = this.appMetadata()?.defaultBankAccount;

    this.bankAccountsStore.setAccounts(bankAccounts, defaultBankAccount);
    this.serviceAccessStore.initFromAccounts(bankAccounts, this.bankAccountsStore.selectedBankAccountId());
    this.customer.set(customer);
  }

  isExpiredToken(token) {
    if (!token) return { isExpired: true };

    const decoded = jwtDecode(token);
    const exp = decoded.exp;
    const date = new Date(exp * 1000);
    const isExpired = new Date() >= date;
    const isAboutToExpire = new Date() >= new Date(exp * 1000 + 600000);

    return { isExpired, isAboutToExpire, decoded };
  }

  logout(reason = 'user_logout') {
    // Modo cookie (Fase 2): pide al backend limpiar la cookie httpOnly (el JS no puede
    // borrarla). Best-effort, sin await: no bloquea el logout local ni la navegación.
    if (environment.useCookieAuth) {
      try {
        this.authService.logout()?.catch?.(() => {});
      } catch {}
    }

    // Send a logout event to all tabs using the StorageEventsService
    this.storageEventsService.emit(LOGOUT_EVENT_KEY, { reason });

    // Execute logout in this tab
    this.performLocalLogout();
  }

  // Method to perform local logout without sending events to other tabs
  private performLocalLogout() {
    this.closingSession.set(true);
    this.zendeskService.logoutUser();
    this.matDialog.closeAll();
    this.matSnackBar.dismiss();

    this.router.navigateByUrl('/login').then(() => {
      this.storageService.remove(STORAGE_KEYS.AUTH_TOKEN);
      // Clear all stores on logout
      setTimeout(() => {
        this.user.set(undefined);
        this.token.set(undefined);
        this.bankAccountsStore.clearStore();
        this.customer.set(undefined);
        this.encryptedPass.set(undefined);
        this.firstExtractionKycPrompted = false;
        this.businessKycPromptShown = false;
        this.clearServiceWorkerCache();
        this.closingSession.set(false);
        this.paymentLinkStore.clearStore();
        // this.debtsStore.clearStore();
        this.movementStore.clearStore();
        this.extractionRequestsStore.clearStore();
        this.beneficiariesStore.clearStore();
        this.bankBeneficiariesStore.clearStore();
        this.bankExtractionRequestsStore.clearStore();
        this.kycEnabledForUser.set(false);
        this.featureFlags.clear();
        // Notify NewPaymentStore to clear its data
        this.eventBusService.emit(CLEAR_NEW_PAYMENT_STORE);
        // Si esta pestaña se está grabando (LogRocket en modo `selected`), la
        // grabación no se puede parar: se fuerza una recarga completa para que
        // el login del siguiente usuario ocurra en una página limpia.
        if (this.sessionRecording.requiresReloadOnLogout) window.location.assign('/login');
      }, 500);
    });
  }

  clearServiceWorkerCache() {
    if ('caches' in window) {
      caches.keys().then((cacheNames) => {
        cacheNames.forEach((cacheName) => {
          caches.delete(cacheName);
        });
      });
    }

    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ action: 'CLEAR_CACHE' });
    }
  }

  /**
   * Pide al backend qué funcionalidades de audiencia restringida le tocan a
   * este usuario. No se espera: la vista se actualiza sola cuando llega, y un
   * fallo deja el KYC oculto en vez de romper la sesión.
   */
  private loadUserFeatureFlags(user: User) {
    this.featureFlags
      .load(String(user.id))
      .then((flags) => this.kycEnabledForUser.set(flags.kycVerification))
      .catch(() => this.kycEnabledForUser.set(false));
  }

  hasRoles(roles: string[]) {
    return roles.every((role) => this.user().roles.includes(role));
  }

  async loadAppConfig() {
    const res = await this.authService.appConfig();
		if (res.data) {
			const map = {}
			res.data.forEach((config) => {
				map[config.key.trim()] = config.value;
			});
      this.appConfig.set(map as any);
			// Arranca LogRocket si la configuración dice 'all'; en modo 'selected'
			// espera a que haya usuario identificado.
			this.sessionRecording.applyConfig(map as any);
		}
  }
}
